import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { BookOpen, Users, BookMarked, LogOut, Plus, Settings, FolderOpen, Shield, Menu, X, Award } from "lucide-react";
import { toast } from "sonner";
import { authFetch, clearAuthToken } from "../utils/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function TeacherDashboard({ user }) {
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const [statsRes, settingsRes] = await Promise.all([
        authFetch('/dashboard/stats'),
        authFetch('/settings/school')
      ]);

      if (statsRes.ok) setStats(await statsRes.json());
      if (settingsRes.ok) setSettings(await settingsRes.json());
    } catch (error) {
      toast.error('Failed to load dashboard');
    }
  };

  const handleLogout = async () => {
    try {
      await authFetch('/auth/logout', { method: 'POST' });
      clearAuthToken();
      toast.success('Logged out successfully');
      navigate('/login');
    } catch (error) {
      clearAuthToken();
      toast.error('Logout failed');
      navigate('/login');
    }
  };

  return (
    <div className="min-h-screen bg-[#FAEDCD]">
      <nav className="bg-[#FEFAE0] border-b border-[#CCD5AE]/50 px-4 sm:px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3">
            <BookOpen className="w-6 h-6 sm:w-8 sm:h-8 text-[#3A5A40]" strokeWidth={1.5} />
            <div>
              <h1 className="text-lg sm:text-2xl font-bold text-[#344E41]" style={{ fontFamily: 'Merriweather, serif' }}>
                {settings?.school_name || 'J423 Community Discipleship Center'}
              </h1>
              <p className="text-xs text-[#588157] hidden sm:block">Teacher Portal</p>
            </div>
          </div>
          
          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 text-[#3A5A40] hover:bg-[#E9EDC9] rounded-lg"
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>

          {/* Desktop navigation */}
          <div className="hidden md:flex items-center gap-4">
            {user?.role === 'admin' && (
              <Button
                variant="ghost"
                onClick={() => navigate('/admin')}
                data-testid="admin-nav-button"
                className="text-[#3A5A40] hover:bg-[#E9EDC9]"
              >
                <Shield className="w-5 h-5 mr-2" strokeWidth={1.5} />
                Admin
              </Button>
            )}
            <Button
              variant="ghost"
              onClick={() => navigate('/modules')}
              data-testid="modules-nav-button"
              className="text-[#3A5A40] hover:bg-[#E9EDC9]"
            >
              <FolderOpen className="w-5 h-5 mr-2" strokeWidth={1.5} />
              Modules
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate('/courses')}
              data-testid="courses-nav-button"
              className="text-[#3A5A40] hover:bg-[#E9EDC9]"
            >
              <BookMarked className="w-5 h-5 mr-2" strokeWidth={1.5} />
              All Courses
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate('/student-certificates')}
              data-testid="student-certificates-nav-button"
              className="text-[#3A5A40] hover:bg-[#E9EDC9]"
            >
              <Award className="w-5 h-5 mr-2" strokeWidth={1.5} />
              Certificates
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate('/settings')}
              data-testid="settings-nav-button"
              className="text-[#3A5A40] hover:bg-[#E9EDC9]"
            >
              <Settings className="w-5 h-5" strokeWidth={1.5} />
            </Button>
            <Avatar className="cursor-pointer" onClick={handleLogout}>
              <AvatarImage src={user?.picture} />
              <AvatarFallback className="bg-[#D4A373] text-white">{user?.name?.[0]}</AvatarFallback>
            </Avatar>
            <Button
              variant="ghost"
              onClick={handleLogout}
              data-testid="logout-button"
              className="text-[#588157] hover:bg-[#E9EDC9]"
            >
              <LogOut className="w-5 h-5" strokeWidth={1.5} />
            </Button>
          </div>
        </div>

        {/* Mobile navigation drawer */}
        {mobileMenuOpen && (
          <div className="md:hidden mt-4 pt-4 border-t border-[#CCD5AE]/50">
            <div className="flex flex-col gap-2">
              {user?.role === 'admin' && (
                <Button
                  variant="ghost"
                  onClick={() => { navigate('/admin'); setMobileMenuOpen(false); }}
                  className="justify-start text-[#3A5A40] hover:bg-[#E9EDC9]"
                >
                  <Shield className="w-5 h-5 mr-2" strokeWidth={1.5} />
                  Admin
                </Button>
              )}
              <Button
                variant="ghost"
                onClick={() => { navigate('/modules'); setMobileMenuOpen(false); }}
                className="justify-start text-[#3A5A40] hover:bg-[#E9EDC9]"
              >
                <FolderOpen className="w-5 h-5 mr-2" strokeWidth={1.5} />
                Modules
              </Button>
              <Button
                variant="ghost"
                onClick={() => { navigate('/courses'); setMobileMenuOpen(false); }}
                className="justify-start text-[#3A5A40] hover:bg-[#E9EDC9]"
              >
                <BookMarked className="w-5 h-5 mr-2" strokeWidth={1.5} />
                All Courses
              </Button>
              <Button
                variant="ghost"
                onClick={() => { navigate('/student-certificates'); setMobileMenuOpen(false); }}
                className="justify-start text-[#3A5A40] hover:bg-[#E9EDC9]"
              >
                <Award className="w-5 h-5 mr-2" strokeWidth={1.5} />
                Student Certificates
              </Button>
              <Button
                variant="ghost"
                onClick={() => { navigate('/settings'); setMobileMenuOpen(false); }}
                className="justify-start text-[#3A5A40] hover:bg-[#E9EDC9]"
              >
                <Settings className="w-5 h-5 mr-2" strokeWidth={1.5} />
                Settings
              </Button>
              <Button
                variant="ghost"
                onClick={handleLogout}
                className="justify-start text-[#588157] hover:bg-[#E9EDC9]"
              >
                <LogOut className="w-5 h-5 mr-2" strokeWidth={1.5} />
                Logout
              </Button>
            </div>
          </div>
        )}
      </nav>

      <main className="max-w-7xl mx-auto p-4 sm:p-6 md:p-12">
        <div className="mb-6 sm:mb-8">
          <h2 className="text-2xl sm:text-4xl font-bold text-[#344E41] mb-2" style={{ fontFamily: 'Merriweather, serif' }}>
            Welcome, {user?.name}
          </h2>
          <p className="text-base sm:text-lg text-[#588157]">Manage your courses and track student progress</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 mb-8 sm:mb-12">
          <Card
            data-testid="stat-my-courses"
            className="p-6 bg-[#FEFAE0] border-[#CCD5AE]/50 shadow-sm hover:shadow-md duration-300 hover:-translate-y-1 rounded-2xl"
          >
            <div className="flex items-center gap-4">
              <div className="p-3 bg-[#3A5A40]/10 rounded-xl">
                <BookOpen className="w-6 h-6 text-[#3A5A40]" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-sm text-[#588157]">My Courses</p>
                <p className="text-3xl font-bold text-[#344E41]">{stats?.my_courses || 0}</p>
              </div>
            </div>
          </Card>

          <Card
            data-testid="stat-total-students"
            className="p-6 bg-[#FEFAE0] border-[#CCD5AE]/50 shadow-sm hover:shadow-md duration-300 hover:-translate-y-1 rounded-2xl"
          >
            <div className="flex items-center gap-4">
              <div className="p-3 bg-[#D4A373]/10 rounded-xl">
                <Users className="w-6 h-6 text-[#D4A373]" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-sm text-[#588157]">Total Students</p>
                <p className="text-3xl font-bold text-[#344E41]">{stats?.total_students || 0}</p>
              </div>
            </div>
          </Card>

          <Card
            data-testid="stat-online-users"
            className="p-6 bg-[#FEFAE0] border-[#CCD5AE]/50 shadow-sm hover:shadow-md duration-300 hover:-translate-y-1 rounded-2xl"
          >
            <div className="flex items-center gap-4">
              <div className="p-3 bg-[#588157]/10 rounded-xl">
                <Users className="w-6 h-6 text-[#588157]" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-sm text-[#588157]">Online Now</p>
                <p className="text-3xl font-bold text-[#344E41]">{stats?.online_users || 0}</p>
              </div>
            </div>
          </Card>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-8">
          <Card className="p-6 sm:p-8 bg-[#FEFAE0] border-[#CCD5AE]/50 shadow-sm hover:shadow-md duration-300 rounded-2xl">
            <div className="text-center">
              <div className="inline-flex p-3 sm:p-4 bg-[#3A5A40]/10 rounded-full mb-4">
                <Plus className="w-8 h-8 sm:w-12 sm:h-12 text-[#3A5A40]" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-[#344E41] mb-2" style={{ fontFamily: 'Merriweather, serif' }}>Create New Course</h3>
              <p className="text-sm sm:text-base text-[#588157] mb-4 sm:mb-6">Share your knowledge and guide students in their faith journey</p>
              <Button
                onClick={() => navigate('/courses/create')}
                data-testid="create-course-button"
                className="bg-[#3A5A40] text-white hover:bg-[#344E41] shadow-md duration-300 hover:shadow-lg active:scale-95 font-medium rounded-lg"
              >
                Create Course
              </Button>
            </div>
          </Card>

          <Card className="p-6 sm:p-8 bg-[#FEFAE0] border-[#CCD5AE]/50 shadow-sm hover:shadow-md duration-300 rounded-2xl">
            <div className="text-center">
              <div className="inline-flex p-3 sm:p-4 bg-[#D4A373]/10 rounded-full mb-4">
                <BookMarked className="w-8 h-8 sm:w-12 sm:h-12 text-[#D4A373]" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-[#344E41] mb-2" style={{ fontFamily: 'Merriweather, serif' }}>View All Courses</h3>
              <p className="text-sm sm:text-base text-[#588157] mb-4 sm:mb-6">Browse and manage existing courses and student enrollments</p>
              <Button
                onClick={() => navigate('/courses')}
                data-testid="view-all-courses-button"
                className="bg-[#DAD7CD] text-[#3A5A40] hover:bg-[#C8C5B8] duration-200 font-medium rounded-lg"
              >
                Browse Courses
              </Button>
            </div>
          </Card>
        </div>
      </main>
    </div>
  );
}